'use client';

import useSWR, { type SWRResponse } from 'swr';
import { transactionsApi } from '@/lib/api';
import type { Transaction } from '@/types';
import { TRANSACTION_SWR_OPTIONS, useAuthedFetcher } from './swrConfig';

/** One widget-card worth of feed rows (see the Últimas transações widget). */
export const HOME_RECENT_TRANSACTIONS_LIMIT = 8;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * The current cycle up to now, as LOCAL dates: without the `to` cap, rows with
 * a future reference date (scheduled charges, credit-card parcelas) sit on top
 * of the desc-sorted feed forever and today's purchases never surface.
 */
export function currentCycleWindow(now: Date = new Date()): { from: string; to: string } {
  const year = now.getFullYear();
  const month = pad(now.getMonth() + 1);
  return {
    from: `${year}-${month}-01`,
    // Millisecond cap — the backend range is inclusive, and a plain :59 would
    // drop rows stamped in the day's last second.
    to: `${year}-${month}-${pad(now.getDate())}T23:59:59.999`,
  };
}

/** How far apart two ingestion stamps can be and still count as one sync run. */
const SYNC_BATCH_WINDOW_MS = 45 * 60 * 1000;
/** How much older the rest of the feed must be for the newest batch to read
 * as "a sync arrived" rather than "the initial import is still one blob". */
const ESTABLISHED_HISTORY_MS = 24 * 60 * 60 * 1000;

/**
 * Ids of the rows the LAST synchronization brought in. Ingestion times
 * (`createdAt`) cluster within minutes per sync run, so the newest batch is
 * everything created within the batch window of the feed's newest stamp.
 * Without genuinely OLDER history in the feed (rows ingested a day+ before
 * the newest, or pre-feature rows without a stamp) there is no "since last
 * sync" story to tell — a first connect / initial import flags nothing, even
 * when its ingestion spans longer than one batch window or fills the whole
 * visible slice.
 */
export function newSinceLastSyncIds(transactions: readonly Transaction[]): ReadonlySet<string> {
  const stamped = transactions.filter(
    (tx): tx is Transaction & { createdAt: string } =>
      !!tx.createdAt && Number.isFinite(Date.parse(tx.createdAt)),
  );
  if (stamped.length === 0) return new Set();

  const newest = Math.max(...stamped.map((tx) => Date.parse(tx.createdAt)));
  const hasOlderHistory = transactions.some(
    (tx) =>
      !tx.createdAt ||
      !Number.isFinite(Date.parse(tx.createdAt)) ||
      Date.parse(tx.createdAt) < newest - ESTABLISHED_HISTORY_MS,
  );
  if (!hasOlderHistory) return new Set();

  const batch = stamped.filter((tx) => Date.parse(tx.createdAt) >= newest - SYNC_BATCH_WINDOW_MS);
  return new Set(batch.map((tx) => tx.id));
}

/**
 * Latest transactions of the CURRENT CYCLE for the New-Home "Últimas
 * transações" widget — a thin, widget-owned slice of `GET /api/transactions/`
 * (cycle window, newest first, small limit).
 *
 * The `'transactions'` key prefix keeps this cache inside the family swept by
 * `revalidateTransactionCaches`, so edits and Open Finance syncs refresh the
 * widget along with every other transaction surface. The window is part of the
 * key, so the feed rolls over naturally at midnight / month turn.
 */
export function useHomeRecentTransactions(
  limit: number = HOME_RECENT_TRANSACTIONS_LIMIT,
): SWRResponse<Transaction[]> {
  const authedFetch = useAuthedFetcher();
  const { from, to } = currentCycleWindow();

  return useSWR<Transaction[]>(
    ['transactions', 'home-recent', limit, from, to],
    async () => {
      const page = await authedFetch((token) =>
        transactionsApi.getAll(token, { from, to, limit, sortField: 'date', sortOrder: 'desc' }),
      );
      return page.items;
    },
    TRANSACTION_SWR_OPTIONS,
  );
}
